import { useEffect, useRef } from 'react';
import { TURNSTILE_SITE_KEY, captchaOn } from '../captcha';

/**
 * Cloudflare's challenge, on the sign-in box.
 *
 * NOTHING AT ALL WITHOUT A KEY. An environment with no key renders no widget,
 * sends no token, and Supabase with no secret asks for none — see captcha.ts
 * for why that is one condition rather than two.
 *
 * THE TOKEN IS USED ONCE. Cloudflare issues one per solve and Supabase rejects
 * a second use, so a failed sign-in leaves a spent token behind and the next
 * attempt fails for a reason that has nothing to do with the password. The
 * widget is therefore reset by whoever used the token, through `resetAt` —
 * changing that number is what says "I have spent it".
 */
declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** Loaded once for the page, not once per render — see the note on remove(). */
let loading: Promise<void> | null = null;
function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!loading) {
    loading = new Promise((done, fail) => {
      const tag = document.createElement('script');
      tag.src = SCRIPT;
      tag.async = true;
      tag.onload = () => done();
      tag.onerror = () => fail(new Error('Cloudflare could not be reached'));
      document.head.appendChild(tag);
    });
  }
  return loading;
}

export default function Turnstile({
  onToken,
  resetAt,
}: {
  onToken: (token: string) => void;
  /** Bump to throw away a spent token and ask for another. */
  resetAt: number;
}) {
  const box = useRef<HTMLDivElement>(null);
  const id = useRef<string | null>(null);
  /*
   * HELD IN A REF, not read from props inside the callback.
   *
   * Cloudflare keeps the callback it was given at render time for the life of
   * the widget. A closure over the first `onToken` would go on calling a
   * setter from a render that no longer exists — the token would arrive and
   * nothing would have it.
   */
  const latest = useRef(onToken);
  latest.current = onToken;

  useEffect(() => {
    if (!captchaOn() || !box.current) return;
    let gone = false;
    void loadTurnstile()
      .then(() => {
        if (gone || !box.current || !window.turnstile) return;
        id.current = window.turnstile.render(box.current, {
          sitekey: TURNSTILE_SITE_KEY,
          // Solves itself for almost everybody and only asks when something
          // looks automated, which is the whole point: the honest person should
          // not be made to click pictures of traffic lights to sign in.
          appearance: 'interaction-only',
          callback: (token: string) => latest.current(token),
          // A challenge that failed or timed out gives nothing back, and the
          // screen must not sit there believing it has a token.
          'error-callback': () => latest.current(''),
          'expired-callback': () => latest.current(''),
        });
      })
      /*
       * CLOUDFLARE BEING UNREACHABLE MUST NOT BE A LOCKED DOOR. If the script
       * does not load there is no token to send, Supabase refuses the sign-in,
       * and the person is told plainly rather than left with a spinner.
       */
      .catch(() => latest.current(''));
    return () => {
      gone = true;
      if (id.current && window.turnstile) window.turnstile.remove(id.current);
      id.current = null;
    };
  }, []);

  // Spent: ask Cloudflare for another. Skipped on the first render, when there
  // is nothing to throw away.
  useEffect(() => {
    if (resetAt && id.current && window.turnstile) window.turnstile.reset(id.current);
  }, [resetAt]);

  if (!captchaOn()) return null;
  return <div className="turnstile" ref={box} />;
}
